import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DoCheck,
  effect,
  forwardRef,
  inject,
  Injector,
  input,
  numberAttribute,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  ValidatorFn,
} from '@angular/forms';
import {
  DateAdapter
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { merge } from 'rxjs';

/** Clés d'erreur que le composant reporte des sous-contrôles vers le contrôle externe. */
const OUTER_ERROR_KEYS = [
  'required',
  'matDatepickerParse',
  'matTimepickerParse',
  'matDatepickerMin',
  'matDatepickerMax',
] as const;

/**
 * Champ composite « date + heure » du thème Pégase : combine le `mat-datepicker` et le
 * `mat-timepicker` natifs d'Angular Material, en remplacement de la bibliothèque tierce
 * `@mat-datetimepicker` (non maintenue et non accessible).
 *
 * S'utilise comme un contrôle de formulaire unique (`ControlValueAccessor` + `Validator`). La
 * valeur exposée est une date/heure complète dont le type dépend du `DateAdapter` fourni par
 * l'application : le composant ne dépend d'aucune bibliothèque de date (toutes les opérations
 * passent par le `DateAdapter` injecté), ce qui lui permet de servir les applications gestionnaire
 * (adaptateur Moment) comme le thème (adaptateur natif). Libellés et messages d'erreur sont des
 * entrées (chaînes déjà traduites) : la bibliothèque n'embarque pas d'identifiants i18n.
 */
@Component({
  selector: 'lib-pegase-date-time-field',
  standalone: true,
  templateUrl: './date-time-field.component.html',
  styleUrls: ['./date-time-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatTimepickerModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimePickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateTimePickerComponent),
      multi: true,
    },
  ],
})
export class DateTimePickerComponent
  implements ControlValueAccessor, Validator, OnInit, DoCheck
{
  readonly dateLabel = input.required<string>();
  readonly timeLabel = input.required<string>();
  readonly hint = input('');
  /** Posé sur l'input date ; l'input heure reçoit la valeur suffixée de `-heure`. */
  readonly name = input('');
  /** Posé sur l'input date ; l'input heure reçoit la valeur suffixée de `-heure`. */
  readonly dataTfa = input('');
  readonly required = input(false, { transform: booleanAttribute });
  readonly readonly = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  /** Pas des heures proposées, **en minutes** (défaut : 5). */
  readonly interval = input(5, { transform: numberAttribute });

  /**
   * Pas converti en secondes : l'entrée `interval` du `mat-timepicker` natif est en secondes.
   * Borné à ≥ 1 minute, sinon la génération des options boucle indéfiniment (gel de l'UI).
   */
  protected readonly intervalSeconds = computed(() => {
    const minutes = this.interval();
    return (
      (Number.isFinite(minutes) && minutes >= 1 ? Math.floor(minutes) : 5) * 60
    );
  });

  readonly parseErrorLabel = input('Date invalide (JJ/MM/AAAA)');
  readonly timeParseErrorLabel = input('Heure invalide (HH:mm)');
  readonly requiredErrorLabel = input('La date est obligatoire');
  readonly minErrorLabel = input('');
  readonly maxErrorLabel = input('');
  /** Erreur métier transverse pilotée par le formulaire appelant (ex : date de début après fin). */
  readonly customError = input<string | null>(null);

  readonly min = input<unknown>(null);
  readonly max = input<unknown>(null);

  readonly dateControl = new FormControl<unknown>(null);
  readonly timeControl = new FormControl<unknown>(null);

  private readonly dateAdapter: DateAdapter<unknown> = inject(DateAdapter);
  private readonly injector = inject(Injector);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly minValue = computed(() => this.coerce(this.min()));
  private readonly maxValue = computed(() => this.coerce(this.max()));

  private ngControl: NgControl | null = null;
  private lastEmittedKey: string | null = null;
  private onChange: (value: unknown) => void = () => undefined;
  private onTouchedCb: () => void = () => undefined;
  private validatorChange: (() => void) | null = null;
  private externalRevalidationScheduled = false;

  /**
   * Min/max sur la valeur date + heure complète (le `mat-datepicker` ne comparerait que la date),
   * porté par le contrôle date pour afficher l'erreur sous le champ.
   */
  private readonly rangeValidator: ValidatorFn = () => {
    const combined = this.combine();
    if (combined == null) {
      return null;
    }
    const min = this.minValue();
    const max = this.maxValue();
    if (min != null && this.compare(combined, min) < 0) {
      return { matDatepickerMin: { min, actual: combined } };
    }
    if (max != null && this.compare(combined, max) > 0) {
      return { matDatepickerMax: { max, actual: combined } };
    }
    return null;
  };

  constructor() {
    this.dateControl.addValidators(this.rangeValidator);
    // Revalide à chaque évolution des bornes ou de `required`.
    effect(() => {
      this.minValue();
      this.maxValue();
      this.required();
      this.revalidate();
    });
    // Le `mat-timepicker` natif recalcule son erreur de format dans un `effect()`, donc APRÈS avoir
    // émis `valueChange` : l'erreur interne peut évoluer sans nouvelle émission (ex : saisie clavier
    // d'une heure valide → contrôle externe figé en erreur, bouton d'enregistrement inactif). On
    // resynchronise la validation externe à chaque changement de statut d'un sous-contrôle.
    merge(this.dateControl.statusChanges, this.timeControl.statusChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.scheduleExternalRevalidation());
  }

  ngOnInit(): void {
    // Hors constructeur pour éviter la dépendance circulaire CVA <-> NgControl.
    this.ngControl = this.injector.get(NgControl, null, {
      optional: true,
      self: true,
    });
  }

  ngDoCheck(): void {
    // Propage le `touched` du contrôle externe (ex : markAllAsTouched à la soumission) pour
    // déclencher l'affichage des `mat-error`.
    if (this.ngControl?.control?.touched && !this.dateControl.touched) {
      this.dateControl.markAsTouched();
      this.timeControl.markAsTouched();
      this.cdr.markForCheck();
    }
  }

  // ------------------------- ControlValueAccessor -------------------------

  writeValue(value: unknown): void {
    const parsed = this.coerce(value);
    // Clé posée avant l'écriture : le `mat-timepicker` natif émet `valueChange` même sur une
    // écriture programmatique ; le dédoublonnage évite de réémettre / marquer « dirty » à tort.
    this.lastEmittedKey = this.emissionKey(parsed);
    if (parsed != null) {
      this.dateControl.setValue(this.dateAdapter.clone(parsed), {
        emitEvent: false,
      });
      this.timeControl.setValue(this.dateAdapter.clone(parsed), {
        emitEvent: false,
      });
    } else {
      this.dateControl.setValue(null, { emitEvent: false });
      this.timeControl.setValue(null, { emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCb = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.dateControl.disable({ emitEvent: false });
      this.timeControl.disable({ emitEvent: false });
    } else {
      this.dateControl.enable({ emitEvent: false });
      this.timeControl.enable({ emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  // ------------------------------ Validator -------------------------------

  validate(): ValidationErrors | null {
    const dateErrors = this.dateControl.errors ?? {};
    const timeErrors = this.timeControl.errors ?? {};
    const errors: ValidationErrors = {};
    if (dateErrors['required'] || timeErrors['required']) {
      errors['required'] = true;
    }
    if (dateErrors['matDatepickerParse']) {
      errors['matDatepickerParse'] = dateErrors['matDatepickerParse'];
    }
    if (timeErrors['matTimepickerParse']) {
      errors['matTimepickerParse'] = timeErrors['matTimepickerParse'];
    }
    if (dateErrors['matDatepickerMin']) {
      errors['matDatepickerMin'] = dateErrors['matDatepickerMin'];
    }
    if (dateErrors['matDatepickerMax']) {
      errors['matDatepickerMax'] = dateErrors['matDatepickerMax'];
    }
    return Object.keys(errors).length ? errors : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.validatorChange = fn;
  }

  // ------------------------------- Template -------------------------------

  onTouched(): void {
    this.onTouchedCb();
  }

  onDateChanged(): void {
    if (this.dateControl.value != null && this.timeControl.value == null) {
      // Date choisie sans heure → minuit.
      this.timeControl.setValue(
        this.dateAdapter.clone(this.dateControl.value),
        { emitEvent: false }
      );
    }
    this.emit();
  }

  onTimeChanged(): void {
    if (this.timeControl.value != null && this.dateControl.value == null) {
      // Heure saisie sans date → date du jour.
      this.dateControl.setValue(this.dateAdapter.today(), { emitEvent: false });
    }
    // Le min/max porté par dateControl dépend de l'heure : on le revalide.
    this.dateControl.updateValueAndValidity({ emitEvent: false });
    this.emit();
  }

  // ------------------------------- Interne --------------------------------

  private emit(): void {
    const combined = this.combine();
    const key = this.emissionKey(combined);
    if (key === this.lastEmittedKey) {
      // Valeur inchangée mais erreurs internes possiblement modifiées : revalider sans réémettre
      // (sinon contrôle marqué « dirty » à tort / NG0100 en dev).
      this.validatorChange?.();
      return;
    }
    this.lastEmittedKey = key;
    this.onChange(combined);
  }

  private combine(): unknown {
    const date = this.dateControl.value;
    if (date == null || !this.dateAdapter.isValid(date)) {
      return null;
    }
    const time = this.timeControl.value;
    const hours = time != null ? this.dateAdapter.getHours(time) : 0;
    const minutes = time != null ? this.dateAdapter.getMinutes(time) : 0;
    return this.dateAdapter.setTime(
      this.dateAdapter.clone(date),
      hours,
      minutes,
      0
    );
  }

  private coerce(value: unknown): unknown {
    if (value == null || value === '') {
      return null;
    }
    const candidate = this.dateAdapter.isDateInstance(value)
      ? value
      : this.dateAdapter.deserialize(value);
    return candidate != null && this.dateAdapter.isValid(candidate)
      ? candidate
      : null;
  }

  /** `toIso8601` ne porte que sur la date : on compare la date puis l'heure (min/max complets). */
  private compare(a: unknown, b: unknown): number {
    const dateA = this.dateAdapter.toIso8601(a);
    const dateB = this.dateAdapter.toIso8601(b);
    if (dateA !== dateB) {
      return dateA < dateB ? -1 : 1;
    }
    return this.minutesOfDay(a) - this.minutesOfDay(b);
  }

  private minutesOfDay(value: unknown): number {
    return (
      this.dateAdapter.getHours(value) * 60 + this.dateAdapter.getMinutes(value)
    );
  }

  private emissionKey(value: unknown): string | null {
    return value == null
      ? null
      : `${this.dateAdapter.toIso8601(value)}T${this.minutesOfDay(value)}`;
  }

  /**
   * Revalide le contrôle externe si les erreurs qu'il porte ne reflètent plus l'état des
   * sous-contrôles. Différé en microtâche : le statut interne évolue pendant la détection de
   * changements (effect du `mat-timepicker`), une revalidation synchrone provoquerait NG0100.
   */
  private scheduleExternalRevalidation(): void {
    if (this.externalRevalidationScheduled) {
      return;
    }
    this.externalRevalidationScheduled = true;
    queueMicrotask(() => {
      this.externalRevalidationScheduled = false;
      const control = this.ngControl?.control;
      if (!control) {
        return;
      }
      const attendues = this.validate() ?? {};
      const actuelles = control.errors ?? {};
      const desynchronise = OUTER_ERROR_KEYS.some(
        (cle) => cle in attendues !== cle in actuelles
      );
      if (!desynchronise) {
        return;
      }
      if (this.validatorChange) {
        this.validatorChange();
      } else {
        control.updateValueAndValidity();
      }
      this.cdr.markForCheck();
    });
  }

  private revalidate(): void {
    this.dateControl.updateValueAndValidity({ emitEvent: false });
    this.validatorChange?.();
    // Un changement de borne ne modifie pas la valeur émise : on force la revalidation externe.
    this.ngControl?.control?.updateValueAndValidity({ emitEvent: false });
  }
}
